import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { showErrorToast, showSuccessToast } from '@ui';
import { translate } from '@utils/i18n';
import { bulkUpdateFileAccess } from '../services/invoiceAttachmentsApi';
import { MASS_ACTIONS_NS } from '../constants';
/**
 * Invoice's one bulk edit — visibility. Delete is not here: it is the mass action every attachment
 * surface has, so it lives in the shared `useAttachmentDeletion`.
 *
 * Always sends the explicit checked-row `ids` array; this listing is unpaginated and fully
 * client-side, so there is no "select all matching filter across pages" mode and every selection
 * is already the complete set of checked ids.
 */
export function useAttachmentBulkEdits({ invoiceId, selectedIds, clearSelection, invalidate }) {
    const fileAccessMutation = useMutation({
        mutationFn: (fileAccess) => bulkUpdateFileAccess(invoiceId, selectedIds, fileAccess),
        onSuccess: () => {
            showSuccessToast(translate('toasts.file_access_success', { namespace: MASS_ACTIONS_NS }));
            clearSelection();
            invalidate();
        },
        // On failure: toast AND still invalidate, so the UI re-syncs with the server rather than
        // showing a stale state. Selection is left as-is since nothing here optimistically changed it.
        onError: () => {
            showErrorToast(translate('toasts.file_access_error', { namespace: MASS_ACTIONS_NS }));
            invalidate();
        }
    });
    return {
        isMutating: fileAccessMutation.isLoading,
        updateFileAccess: useCallback((fileAccess) => fileAccessMutation.mutate(fileAccess), [fileAccessMutation])
    };
}
